// chatSeedBus — tiny sessionStorage-backed handoff used to drop a
// pre-built "system primer" into the Hub Chat panel when the user
// arrives via `/workspace?tab=chat&seed=…`.
//
// Why sessionStorage and not URL params: playbook bodies can be big
// (multi-step guides, several KB of markdown), and we don't want them
// in the address bar. We also want them to survive the React-Router
// navigation but NOT a hard refresh (a refresh = "I want a clean
// chat" — the seed is a one-shot handoff, not a saved conversation).
//
// Shape:
//   - `primer`   markdown shown as a synthetic assistant message at the
//                top of the conversation. Includes the playbook title +
//                step list so the model has full context.
//   - `draft`    starter user message we pre-fill in the textarea so
//                the user can hit Send immediately. Editable.
//   - `source`   label like "library:fork-to-chat" — used for analytics
//                + the "Loaded from …" pill.
export interface ChatSeed {
  primer: string;
  draft: string;
  source: string;
  /** Optional — the library entry id, surfaced in the seed pill. */
  entryId?: string;
  /** Optional — the playbook title, surfaced in the seed pill. */
  title?: string;
  /** When true, the consuming chat shell should auto-send the draft as
   *  the user's first turn immediately after seeding so the assistant
   *  starts coaching without the learner needing to hit Send. Used by
   *  the "click to chat" entry points on quests + daily challenges. */
  autoSend?: boolean;
}

const KEY = "hl.hub.chat.seed";

export const stashChatSeed = (seed: ChatSeed) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(seed));
  } catch {
    /* ignore — quota / disabled storage is fine, just lose the seed */
  }
};

/** Pop = read + delete. Seeds are single-use. */
export const popChatSeed = (): ChatSeed | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    return JSON.parse(raw) as ChatSeed;
  } catch {
    return null;
  }
};
